import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './PageBuilder.module.css';
import { API } from '../config';

import HeadingBlock from './blocks/HeadingBlock';
import ParagraphBlock from './blocks/ParagraphBlock';
import ListBlock from './blocks/ListBlock';
import TableBlock from './blocks/TableBlock';
import CodeBlock from './blocks/CodeBlock';
import ImageBlock from './blocks/ImageBlock';
import YouTubeBlock from './blocks/YouTubeBlock';

const PageBuilder = () => {
  const { id: pageId } = useParams();
  const navigate = useNavigate();

  const [blocks, setBlocks] = useState([]);
  const [title, setTitle] = useState('');
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [parentPages, setParentPages] = useState([]);
  const [parentId, setParentId] = useState('');

  useEffect(() => {
    fetchCourses();
    fetchParentPages();
    if (pageId) fetchPageData();
  }, [pageId]);

  const fetchCourses = async () => {
    try {
      const res = await fetch(API.GET_COURSES);
      const data = await res.json();
      setCourses(Array.isArray(data.courses) ? data.courses : []);
    } catch (err) {
      console.error("Failed to fetch courses", err);
    }
  };

  const fetchParentPages = async () => {
    try {
      const res = await fetch(API.GET_PARENT_PAGES);
      const data = await res.json();
      setParentPages(Array.isArray(data.pages) ? data.pages : []);
    } catch (err) {
      console.error("Failed to fetch parent pages", err);
    }
  };

  const fetchPageData = async () => {
    try {
      const res = await fetch(`${API.GET_PAGE_BY_ID}?page_id=${pageId}`);
      const data = await res.json();
      if (data.success) {
        setTitle(data.page.title || '');
        setSelectedCourse(data.page.course_id || '');
        setParentId(data.page.parent_id || '');
        const parsed = JSON.parse(data.page.content_json || '[]');
        setBlocks(Array.isArray(parsed) ? parsed : []);
      }
    } catch (err) {
      console.error("Failed to load page data", err);
    }
  };

  const renderNestedOptions = (pages, depth = 0) => {
    return pages.flatMap((page) => {
      const indent = '\u00A0\u00A0'.repeat(depth);
      const current = (
        <option key={page.id} value={page.id}>
          {indent}↳ {page.title}
        </option>
      );
      const children = page.children ? renderNestedOptions(page.children, depth + 1) : [];
      return [current, ...children];
    });
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await fetch(`${API.BASE}/upload-image.php`, {
        method: "POST",
        body: formData,
      });
      const result = await res.json();
      if (result.success) {
        const updatedBlocks = [...blocks];
        updatedBlocks[index] = { ...updatedBlocks[index], url: result.url };
        setBlocks(updatedBlocks);
      } else {
        alert("Upload failed: " + result.message);
      }
    } catch (err) {
      alert("Image upload error");
    }
  };

  const addBlock = (type) => {
    const newBlock = { type, content: '' };
    if (type === 'table') newBlock.rows = [['']];
    if (type === 'list') newBlock.items = [''];
    if (type === 'image') newBlock.url = '';
    setBlocks([...blocks, newBlock]);
  };

  const updateBlock = (index, updatedBlock) => {
    const newBlocks = [...blocks];
    newBlocks[index] = updatedBlock;
    setBlocks(newBlocks);
  };

  const deleteBlock = (index) => {
    const newBlocks = blocks.filter((_, i) => i !== index);
    setBlocks(newBlocks);
  };

  const handleSubmit = async () => {
    if (!title || !selectedCourse) {
      alert("Please fill all required fields");
      return;
    }

    const payload = {
      id: pageId,
      title,
      course_id: selectedCourse,
      parent_id: parentId || null,
      blocks
    };

    try {
      const res = await fetch(pageId ? API.UPDATE_PAGE : API.ADD_PAGE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        alert(pageId ? 'Page updated!' : 'Page saved!');
        navigate(`/pages`);
      } else {
        alert("Failed to save page");
      }
    } catch (err) {
      alert("Server error");
      console.error(err);
    }
  };

  return (
    <div className={styles.pageBuilder}>
      <h2>{pageId ? 'Edit Tutorial Page' : 'Create Tutorial Page'}</h2>

      <input
        type="text"
        value={title}
        placeholder="Enter page title"
        onChange={(e) => setTitle(e.target.value)}
        className={styles.titleInput}
      />

      <select
        value={selectedCourse}
        onChange={(e) => setSelectedCourse(e.target.value)}
        className={styles.dropdown}
      >
        <option value="">Select Course</option>
        {courses.map(course => (
          <option key={course.id} value={course.id}>{course.coursename}</option>
        ))}
      </select>

      <select
        value={parentId}
        onChange={(e) => setParentId(e.target.value)}
        className={styles.dropdown}
      >
        <option value="">Parent (None)</option>
        {renderNestedOptions(parentPages)}
      </select>

      <div className={styles.buttonGroup}>
        <button onClick={() => addBlock('heading')}>Add Heading</button>
        <button onClick={() => addBlock('paragraph')}>Add Paragraph</button>
        <button onClick={() => addBlock('list')}>Add List</button>
        <button onClick={() => addBlock('table')}>Add Table</button>
        <button onClick={() => addBlock('code')}>Add Code Block</button>
        <button onClick={() => addBlock('image')}>Add Image</button>
         <button onClick={() => addBlock('youtube')}>Add YT Link</button>
      </div>

      <div className={styles.editorPreviewWrapper}>
        <div className={styles.editor}>
          {blocks.map((block, i) => (
            <div key={i} className={styles.blockWrapper}>
              {block.type === 'heading' && (
                <HeadingBlock block={block} onChange={(b) => updateBlock(i, b)} />
              )}
              {block.type === 'paragraph' && (
                <ParagraphBlock block={block} onChange={(b) => updateBlock(i, b)} />
              )}
              {block.type === 'list' && (
                <ListBlock block={block} onChange={(b) => updateBlock(i, b)} />
              )}
              {block.type === 'table' && (
                <TableBlock block={block} onChange={(b) => updateBlock(i, b)} />
              )}
              {block.type === 'code' && (
                <CodeBlock block={block} onChange={(b) => updateBlock(i, b)} />
              )}
              {block.type === 'image' && (
                <div>
                  <ImageBlock block={block} onChange={(b) => updateBlock(i, b)} />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, i)}
                  />
                </div>
              )}
              {block.type === 'youtube' && (
  <YouTubeBlock block={block} onChange={(b) => updateBlock(i, b)} />
)}

              <button className={styles.deleteBtn} onClick={() => deleteBlock(i)}>❌ Remove</button>
            </div>
          ))}
          <button className={styles.saveBtn} onClick={handleSubmit}>
            💾 {pageId ? 'Update' : 'Save'} Page
          </button>
        </div>

        <div className={styles.preview}>
          <h2>{title}</h2>
          {blocks.map((block, i) => {
            switch (block.type) {
              case 'heading': {
                const Tag = block.level || 'h2';
                return React.createElement(Tag, { key: i }, block.content);
              }
              case 'paragraph':
                return (
                  <div
                    key={i}
                    dangerouslySetInnerHTML={{ __html: block.content }}
                  />
                );
              case 'list':
                return (
                  <ul key={i}>
                    {block.items.map((item, j) => (
                      <li key={j} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                );
              case 'table':
                return (
                  <table key={i} border="1">
                    <tbody>
                      {block.rows.map((row, r) => (
                        <tr key={r}>
                          {row.map((cell, c) => (
                            <td key={c} dangerouslySetInnerHTML={{ __html: cell }} />
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              case 'image':
                return (
                  <div key={i}>
                    {block.url ? (
                      <img
                        src={block.url}
                        alt={block.alt || 'Uploaded Image'}
                        style={{ maxWidth: '100%', height: 'auto' }}
                      />
                    ) : (
                      <p>No image</p>
                    )}
                  </div>
                );
              case 'youtube': {
  const videoIdMatch = block.url?.match(
    /(?:youtube\.com\/(?:[^\/]+\/.+|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
  );
  const videoId = videoIdMatch ? videoIdMatch[1] : null;

  return videoId ? (
    <div key={i} style={{ margin: '20px 0', textAlign: 'center' }}>
      <iframe
        width="100%"
        height="400"
        src={`https://www.youtube.com/embed/${videoId}`}
        title="YouTube Video"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    </div>
  ) : (
    <p key={i} style={{ color: 'red' }}>Invalid YouTube URL</p>
  );
}


              case 'code':
                return <pre key={i}><code>{block.content}</code></pre>;
              default:
                return null;
            }
          })}
        </div>
      </div>
    </div>
  );
};

export default PageBuilder;
